import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { formatDate, formatDateTime } from "@/lib/format";
import { CalendarPlus, Upload, Trash2, ArrowRight } from "lucide-react";
import { toast } from "sonner";

const STATUS_COLOR = {
  review: "bg-amber-100 text-amber-800 border-amber-200",
  committed: "bg-green-100 text-green-800 border-green-200",
  archived: "bg-slate-100 text-slate-700 border-slate-200",
};

const monthLabel = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("en-IN", { month: "long", year: "numeric" });
};

export default function AttendanceList() {
  const { user } = useAuth();
  const navigate = useNavigate();
  // Rev. 2026-02 — Include hr_admin & hr_maker (maker-checker roles).
  const canEdit = ["super_admin", "hr", "hr_admin", "hr_maker"].includes(user?.role);
  const [uploads, setUploads] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/attendance/uploads");
      setUploads(data);
    } catch {
      toast.error("Failed to load uploads");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const onDelete = async (id) => {
    if (!window.confirm("Delete this attendance upload? All daily records and summary will be removed.")) return;
    try {
      await api.delete(`/attendance/uploads/${id}`);
      toast.success("Upload deleted");
      load();
    } catch (e) {
      toast.error("Failed to delete");
    }
  };

  return (
    <div data-testid="attendance-list-page" className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-slate-400">Phase 2</div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900" style={{ fontFamily: "Chivo" }}>
            Attendance
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Upload monthly biometric files, review anomalies, finalise to monthly summary.
          </p>
        </div>
        {canEdit && (
          <Link to="/attendance/upload">
            <Button data-testid="new-upload-btn" className="brand-bg brand-bg-hover text-white">
              <Upload className="h-4 w-4 mr-2" />
              New upload
            </Button>
          </Link>
        )}
      </div>

      <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
        <table className="w-full text-sm" data-testid="uploads-table">
          <thead className="bg-slate-50 border-b">
            <tr className="text-left text-[11px] uppercase tracking-wider text-slate-500">
              <th className="px-4 py-3">Month</th>
              <th className="px-4 py-3">Unit</th>
              <th className="px-4 py-3">File</th>
              <th className="px-4 py-3 text-right">Matched</th>
              <th className="px-4 py-3 text-right">Unmatched</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3">Uploaded</th>
              <th className="px-4 py-3 w-12"></th>
              <th className="px-4 py-3 w-12"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading && (
              <tr><td colSpan={9} className="px-4 py-12 text-center text-slate-400">Loading...</td></tr>
            )}
            {!loading && uploads.length === 0 && (
              <tr>
                <td colSpan={9} className="px-4 py-12 text-center text-slate-500">
                  No uploads yet. {canEdit && (
                    <Link to="/attendance/upload" className="brand-text underline font-medium">Upload your first file</Link>
                  )}
                </td>
              </tr>
            )}
            {uploads.map((u) => (
              <tr key={u.id} className="hover:bg-slate-50">
                <td className="px-4 py-3 font-medium text-slate-900">
                  <CalendarPlus className="h-3.5 w-3.5 text-slate-400 inline mr-2" />
                  {monthLabel(u.month)}
                </td>
                <td className="px-4 py-3 text-slate-700">{u.unit_name}</td>
                <td className="px-4 py-3 text-xs text-slate-500 font-mono truncate max-w-xs">{u.filename}</td>
                <td className="px-4 py-3 text-right tabular-nums">{u.matched_count}</td>
                <td className="px-4 py-3 text-right tabular-nums">
                  {u.unmatched_punch_ids > 0 ? (
                    <span className="text-red-700 font-medium">{u.unmatched_punch_ids}</span>
                  ) : (
                    <span className="text-slate-400">0</span>
                  )}
                </td>
                <td className="px-4 py-3">
                  <Badge variant="outline" className={`${STATUS_COLOR[u.status] || ""} border uppercase text-[10px]`}>
                    {u.status}
                  </Badge>
                </td>
                <td className="px-4 py-3 text-xs text-slate-500">
                  <div>{formatDateTime(u.uploaded_at)}</div>
                  <div className="text-[10px] text-slate-400">{u.uploaded_by_email}</div>
                </td>
                <td className="px-4 py-3">
                  <button
                    data-testid={`open-${u.id}`}
                    onClick={() => navigate(`/attendance/review/${u.id}`)}
                    className="p-1 hover:bg-slate-100 rounded"
                  >
                    <ArrowRight className="h-4 w-4 text-slate-400" />
                  </button>
                </td>
                <td className="px-4 py-3">
                  {canEdit && (
                    <button
                      data-testid={`del-${u.id}`}
                      onClick={() => onDelete(u.id)}
                      className="p-1 hover:bg-red-50 rounded text-red-600"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
